// Structural validation for a journey config.
//
// Runs at registration, so a journey loaded from JSON — or a typo in one of
// the checked-in configs — fails at start-up with a message naming the step,
// rather than as a screen that renders and is quietly wrong. The same checks
// run in `__tests__/journeys.test.js` for the built-in verticals; this is the
// runtime half, for configs the test suite has never seen.

/** @typedef {import("../../form/types").JourneyConfig} JourneyConfig */

const fail = (where, message) => {
  throw new Error(`Invalid journey config (${where}): ${message}`);
};

const isPath = (value) => typeof value === "string" && value.startsWith("/");

/**
 * @param {JourneyConfig} journey
 */
export const assertJourney = (journey) => {
  if (!journey || typeof journey !== "object") fail("journey", "not an object");
  const where = `journey "${journey.key}"`;
  if (!journey.key || !/^[a-z][a-z0-9-]*$/.test(journey.key)) fail(where, "key must be a lowercase slug");
  if (!journey.label) fail(where, "label is required");
  if (!isPath(journey.entry)) fail(where, "entry must be a route starting with /");
  if (!Array.isArray(journey.steps) || !journey.steps.length) fail(where, "steps must be a non-empty array");

  const keys = new Set();
  const paths = new Set();
  journey.steps.forEach((step, index) => {
    const at = `${where} step[${index}] "${step?.key}"`;
    if (!step || typeof step !== "object") fail(at, "not an object");
    if (!step.key) fail(at, "key is required");
    if (keys.has(step.key)) fail(at, "duplicate step key");
    keys.add(step.key);
    if (!step.label) fail(at, "label is required");
    if (!isPath(step.path)) fail(at, "path must start with /");
    if (paths.has(step.path)) fail(at, `path ${step.path} is used by another step`);
    paths.add(step.path);
    if (step.next !== undefined && !isPath(step.next)) fail(at, "next must be a route");
    if (step.layout !== undefined && !["form", "wide"].includes(step.layout)) fail(at, `unknown layout "${step.layout}"`);
    if (!step.form && !step.screen) fail(at, "a step needs either a form or a screen");

    for (const alias of step.aliasPaths || []) {
      const path = typeof alias === "string" ? alias : alias?.path;
      if (!isPath(path)) fail(at, "every aliasPaths entry needs a path starting with /");
    }

    if (step.form) {
      if (!step.form.id) fail(at, "form.id is required");
      if (!Array.isArray(step.form.sections)) fail(at, "form.sections must be an array");
      const names = new Set();
      step.form.sections.forEach((section, s) => {
        if (!Array.isArray(section?.fields)) fail(at, `section[${s}].fields must be an array`);
        section.fields.forEach((field, f) => {
          const fat = `${at} section[${s}] field[${f}]`;
          if (!field?.name) fail(fat, "name is required");
          if (!field.type) fail(fat, `"${field.name}" has no type`);
          if (names.has(field.name)) fail(fat, `"${field.name}" is declared twice`);
          names.add(field.name);
          if (field.options && !Array.isArray(field.options)) {
            if (field.options.source !== "reference" || !field.options.domain) {
              fail(fat, `"${field.name}" options must be an array or { source: "reference", domain }`);
            }
          }
        });
      });
      for (const name of step.summary || []) {
        if (!names.has(name)) fail(at, `summary names "${name}", which the form does not ask for`);
      }
    }
  });
};

export default assertJourney;
